/**
 * FileAttachment — compact file card for chat messages.
 * Shows file icon, name, size, and download link.
 * Images show an inline thumbnail with lightbox on click.
 */
import React, { useState } from "react"

export interface AttachmentData {
  fileName: string
  fileType: string
  fileSize: number
  downloadUrl: string
  previewUrl?: string
}

interface FileAttachmentProps {
  attachment: AttachmentData
  compact?: boolean
}

const FILE_ICONS: Record<string, string> = {
  "image/": "image",
  "application/pdf": "picture_as_pdf",
  "text/": "description",
  "application/json": "data_object",
  "application/vnd.openxmlformats": "table_chart",
}

function getFileIcon(type: string): string {
  for (const [prefix, icon] of Object.entries(FILE_ICONS)) {
    if (type.startsWith(prefix)) return icon
  }
  return "attach_file"
}

function formatSize(bytes: number): string {
  if (bytes < 1024) return `${bytes} B`
  if (bytes < 1024 * 1024) return `${(bytes / 1024).toFixed(1)} KB`
  return `${(bytes / (1024 * 1024)).toFixed(1)} MB`
}

export const FileAttachment = React.memo(function FileAttachment({
  attachment,
  compact = false,
}: FileAttachmentProps) {
  const [lightboxOpen, setLightboxOpen] = useState(false)
  const isImage = attachment.fileType.startsWith("image/")
  const imageUrl = attachment.previewUrl || attachment.downloadUrl

  return (
    <>
      <div
        className={`
          inline-flex items-center gap-2 px-2.5 py-2
          bg-[var(--orca-bg-surface)] border border-[var(--orca-border-subtle)]
          rounded-[var(--orca-radius-sm)] transition-colors
          hover:border-[var(--orca-border-medium)]
          ${compact ? "max-w-[200px]" : "max-w-[280px]"}
        `}
      >
        {/* Thumbnail or icon */}
        {isImage && imageUrl ? (
          <button
            onClick={() => setLightboxOpen(true)}
            className="shrink-0 w-10 h-10 rounded-[var(--orca-radius-sm)] overflow-hidden bg-[var(--orca-bg-deep)] cursor-pointer"
          >
            <img
              src={imageUrl}
              alt={attachment.fileName}
              className="w-full h-full object-cover"
              loading="lazy"
            />
          </button>
        ) : (
          <span className="material-icons text-lg text-[var(--orca-text-muted)] shrink-0">
            {getFileIcon(attachment.fileType)}
          </span>
        )}

        {/* File info */}
        <div className="flex-1 min-w-0">
          <div className="text-[11px] font-medium text-[var(--orca-text-primary)] truncate">
            {attachment.fileName}
          </div>
          <div className="text-[10px] text-[var(--orca-text-muted)]">
            {formatSize(attachment.fileSize)}
          </div>
        </div>

        {/* Download */}
        <a
          href={attachment.downloadUrl}
          target="_blank"
          rel="noopener noreferrer"
          className="material-icons text-sm text-[var(--orca-text-muted)] hover:text-[var(--orca-accent)] transition-colors shrink-0"
          title="Download"
        >
          download
        </a>
      </div>

      {/* Image lightbox */}
      {lightboxOpen && isImage && imageUrl && (
        <div
          className="fixed inset-0 z-[100] flex items-center justify-center bg-black/80 cursor-pointer"
          onClick={() => setLightboxOpen(false)}
        >
          <button
            className="absolute top-4 right-4 text-white/80 hover:text-white"
            onClick={() => setLightboxOpen(false)}
          >
            <span className="material-icons text-3xl">close</span>
          </button>
          <img
            src={attachment.downloadUrl}
            alt={attachment.fileName}
            className="max-w-[90vw] max-h-[90vh] object-contain rounded-lg shadow-2xl"
            onClick={(e) => e.stopPropagation()}
          />
        </div>
      )}
    </>
  )
})
